import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, getDocs, onSnapshot, writeBatch, serverTimestamp, doc, getDoc, addDoc, orderBy, Timestamp, collectionGroup, deleteField, setDoc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../../utils/firebase';
import { School, Teacher, User, isTeacher, isSuperAdmin } from '../../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import TimetableEditDialog from '../../../components/ui/TimetableEditDialog';
import PlusCircleIcon from '../../../components/icons/PlusCircleIcon';
import DividerIcon from '../../../components/icons/DividerIcon';
import EditIcon from '../../../components/icons/EditIcon';
import { getSubjectName } from '../../../utils/subjectUtils';
import AddNewClassDialog from '../../../components/ui/AddNewClassDialog';

interface RankData {
  id: string;
  name: string;
  points: number;
  stamps: number | { count: number };
  studentNumber?: number;
  grade?: number;
}

// 타입 가드 함수 추가
const isStampObject = (stamps: number | { count: number }): stamps is { count: number } => {
  return typeof stamps === 'object' && stamps !== null && 'count' in stamps;
};

interface PointHistory {
  id: string;
  studentId: string;
  studentName: string;
  amount: number;
  type: 'add' | 'subtract' | 'use' | 'oneTime';
  reason: string;
  createdAt: Date;
  teacherId: string;
  teacherName: string;
}

// 아이템 사용 승인 요청 인터페이스 추가
interface ItemApprovalRequest {
  id: string;
  studentId: string;
  studentName: string;
  itemId: string;
  itemName: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  updatedAt?: Date;
  reason?: string;
  targetType: 'class' | 'subject';
  studentClassId: string;
  studentGrade: number;
  studentClass: number;
}

interface TeacherUser {
  id: string;
  name: string;
  email: string;
  role: string;
  schoolId: string;
  homeroomClass?: {
    id: string;
    grade: number;
    class: number;
  };
  subject?: 'art' | 'tech';
  classId?: string;
  subjectId?: string;
  uid: string;
}

const SubjectTeacherHome: React.FC = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<School | null>(null);
  const [pointRankings, setPointRankings] = useState<RankData[]>([]);
  const [stampRankings, setStampRankings] = useState<RankData[]>([]);
  const [openAddNewClassDialog, setOpenAddNewClassDialog] = useState(false);
  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(false);
  
  // 학생 상세 정보 다이얼로그 상태
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<RankData | null>(null);
  const [pointHistory, setPointHistory] = useState<PointHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // 아이템 승인 요청 관련 상태 추가
  const [approvalRequests, setApprovalRequests] = useState<ItemApprovalRequest[]>([]);
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ItemApprovalRequest | null>(null);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [studentCount, setStudentCount] = useState<number>(0);
  const [schedules, setSchedules] = useState<Array<{
    id: string;
    title: string;
    description: string;
    time?: string;
    date?: number; // 날짜 추가
  }>>([]);

  // 시간표 다이얼로그 상태 추가
  const [isTimetableDialogOpen, setIsTimetableDialogOpen] = useState(false);
  
  // 학년 필터 상태 추가
  const [stampGradeFilter, setStampGradeFilter] = useState<'all' | 1 | 2 | 3>('all');
  const [approvalGradeFilter, setApprovalGradeFilter] = useState<'all' | 1 | 2 | 3>('all');
  
  // 교과 교실 목록 상태 추가
  const [subjectClasses, setSubjectClasses] = useState<Array<{
    id: string;
    grade: number;
    class: number;
    studentCount: number;
  }>>([]);
  
  // 시간표 데이터 상태
  const [timetableData, setTimetableData] = useState<{
    semester: '1학기' | '2학기';
    year: number;
    entries: {
      period: number;
      time: string;
      monday?: string;
      tuesday?: string;
      wednesday?: string;
      thursday?: string;
      friday?: string;
    }[];
  }>({
    semester: '1학기',
    year: new Date().getFullYear(),
    entries: [
      { period: 1, time: '09:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 2, time: '10:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '영어', friday: '통합 과학' },
      { period: 3, time: '11:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 4, time: '12:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 5, time: '13:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 6, time: '14:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 7, time: '15:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
    ]
  });

  // 달력 생성 함수
  const generateCalendar = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // 현재 월의 첫 번째 날과 마지막 날
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 첫 번째 날의 요일 (0=일요일, 1=월요일, ...)
    const firstDayOfWeek = firstDay.getDay();
    
    // 현재 월의 일수
    const daysInMonth = lastDay.getDate();
    
    // 이전 달의 마지막 날들 (빈 칸 채우기용)
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    const calendar = [];
    
    // 이전 달의 날짜들 (빈 칸 채우기)
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      calendar.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
      calendar.push({
        date: day,
        isCurrentMonth: true,
        isToday
      });
    }
    
    // 다음 달의 날짜들 (빈 칸 채우기)
    const remainingCells = 42 - calendar.length; // 6주 * 7일 = 42칸
    for (let day = 1; day <= remainingCells; day++) {
      calendar.push({
        date: day,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return calendar;
  };
  
  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !isTeacher(currentUser)) return;

      try {
        setLoading(true);
        
        // 학교 정보 가져오기
        const schoolRef = doc(db, 'schools', currentUser.schoolId);
        const schoolDoc = await getDoc(schoolRef);
        
        if (schoolDoc.exists()) {
          setSchoolInfo(schoolDoc.data() as School);
        }

        // 교과 담당인 경우 학생들의 도장 순위 가져오기
        if (currentUser.subject && currentUser.subjectId) {
          const studentsRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`);
          const studentsSnapshot = await getDocs(studentsRef);
          
          // 학생 수 설정
          setStudentCount(studentsSnapshot.size);

          // 교실별 학생 수 맵 (과목 수강 학생 기준)
          const studentCountByClass = new Map<string, number>();
          studentsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const grade = data.grade || 1;
            const classNum = data.class || 1;
            const key = `${grade}_${classNum}`;
            studentCountByClass.set(key, (studentCountByClass.get(key) || 0) + 1);
          });

          // 담당 교실 목록: 교사가 만든 subjectClasses에서 로드 (새로 만든 교실도 표시)
          const subjectClassesRef = collection(db, `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectClasses`);
          const subjectClassesSnapshot = await getDocs(subjectClassesRef);
          const classesArray = subjectClassesSnapshot.docs
            .map((d) => {
              const data = d.data();
              const grade = data.grade ?? 1;
              const classNum = data.class ?? 1;
              const id = d.id;
              return {
                id,
                grade,
                class: classNum,
                studentCount: studentCountByClass.get(id) ?? 0
              };
            })
            .sort((a, b) => {
              if (a.grade !== b.grade) return a.grade - b.grade;
              return a.class - b.class;
            });
          setSubjectClasses(classesArray);

          // 시간표 불러오기 (교사별 교과 시간표)
          const timetableRef = doc(db, `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectTimetable/current`);
          const timetableDoc = await getDoc(timetableRef);
          
          if (timetableDoc.exists()) {
            const timetableData = timetableDoc.data();
            setTimetableData({
              semester: timetableData.semester || '1학기',
              year: timetableData.year || new Date().getFullYear(),
              entries: timetableData.entries || []
            });
          }
          
          const studentsData = studentsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name,
              points: data.points || 0,
              stamps: data.stamps || 0,
              studentNumber: data.number || null
            };
          }) as RankData[];

          // 포인트 기준으로 내림차순 정렬
          const sortedByPoints = [...studentsData].sort((a, b) => {
            const aPoints = typeof a.points === 'number' ? a.points : 0;
            const bPoints = typeof b.points === 'number' ? b.points : 0;
            return bPoints - aPoints;
          });
          setPointRankings(sortedByPoints);

          // 도장 기준으로 내림차순 정렬
          const sortedByStamps = [...studentsData].sort((a, b) => {
            const aStamps = isStampObject(a.stamps) ? a.stamps.count : a.stamps;
            const bStamps = isStampObject(b.stamps) ? b.stamps.count : b.stamps;
            return bStamps - aStamps;
          });
          setStampRankings(sortedByStamps);
        }

        // 교과 도장 순위 가져오기 (subjectId 필수)
        if (currentUser.subject && currentUser.subjectId) {
          const subjectStudentsRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`);
          const subjectStudentsSnapshot = await getDocs(subjectStudentsRef);
          
          const subjectStudentsData = subjectStudentsSnapshot.docs.map(doc => {
            const data = doc.data();
            console.log('Raw student data:', data);
            
            // stampCount 처리
            let stampCount = 0;
            if (typeof data.stampCount === 'object' && data.stampCount !== null) {
              stampCount = data.stampCount.count || 0;
            } else if (typeof data.stampCount === 'number') {
              stampCount = data.stampCount;
            }

            return {
              id: doc.id,
              name: data.name || '이름 없음',
              points: 0,
              stamps: stampCount,
              grade: data.grade || 1
            };
          }) as RankData[];

          console.log('Processed subject students data:', subjectStudentsData);

          // 도장 기준으로 내림차순 정렬
          const sortedByStamps = [...subjectStudentsData].sort((a, b) => {
            const aStamps = isStampObject(a.stamps) ? a.stamps.count : a.stamps;
            const bStamps = isStampObject(b.stamps) ? b.stamps.count : b.stamps;
            return bStamps - aStamps;
          });
          setStampRankings(sortedByStamps);
        }

        let unsubscribeSubjectApprovals: (() => void) | undefined;

        // 교과 아이템 승인 요청 가져오기 (subjectId가 있을 때만)
        if (currentUser.subjectId) {
          const subjectApprovalQuery = query(
            collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/subjectitemApprovals`),
            where('status', '==', 'pending')
          );

          unsubscribeSubjectApprovals = onSnapshot(subjectApprovalQuery, (snapshot) => {
            const subjectRequests = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate(),
              updatedAt: doc.data().updatedAt?.toDate(),
              targetType: 'subject',
              studentClassId: doc.data().studentClassId || ''
            })) as ItemApprovalRequest[];
            
            setApprovalRequests(subjectRequests);
          });
        }

         // 임시 일정 데이터 (실제로는 Firestore에서 가져와야 함)
         const today = new Date();
         setSchedules([
           {
             id: '1',
             title: '수학 시험',
             description: '1교시 수학 시험 진행',
             time: '09:00',
             date: today.getDate() // 오늘 날짜에 일정
           },
           {
             id: '2', 
             title: '체육 수업',
             description: '3교시 체육 수업',
             time: '10:30',
             date: today.getDate() + 1 // 내일 날짜에 일정
           }
         ]);

        return () => {
          unsubscribeSubjectApprovals?.();
        };
      } catch (err) {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // 담당 교실 목록만 다시 불러오기 (교실 추가 후 목록 갱신용)
  const refetchSubjectClasses = React.useCallback(async () => {
    if (!currentUser?.schoolId || !currentUser?.subjectId || !isTeacher(currentUser)) return;
    try {
      const studentsRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`);
      const studentsSnapshot = await getDocs(studentsRef);
      const studentCountByClass = new Map<string, number>();
      studentsSnapshot.docs.forEach((d) => {
        const data = d.data();
        const key = `${data.grade ?? 1}_${data.class ?? 1}`;
        studentCountByClass.set(key, (studentCountByClass.get(key) || 0) + 1);
      });
      const subjectClassesRef = collection(db, `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectClasses`);
      const subjectClassesSnapshot = await getDocs(subjectClassesRef);
      const classesArray = subjectClassesSnapshot.docs
        .map((d) => {
          const data = d.data();
          const grade = data.grade ?? 1;
          const classNum = data.class ?? 1;
          return { id: d.id, grade, class: classNum, studentCount: studentCountByClass.get(d.id) ?? 0 };
        })
        .sort((a, b) => (a.grade !== b.grade ? a.grade - b.grade : a.class - b.class));
      setSubjectClasses(classesArray);
    } catch (err) {
      console.error('담당 교실 목록 다시 불러오기 오류:', err);
    }
  }, [currentUser]);

  // 학생 클릭 시 상세 정보 다이얼로그 열기
  const handleStudentClick = async (student: RankData) => {
    setSelectedStudent(student);
    setOpenStudentDialog(true);
    setLoadingHistory(true);
    
    try {
      if (!currentUser?.schoolId || !currentUser?.subjectId) {
        console.error('학교 ID 또는 교과 ID가 없습니다:', { schoolId: currentUser?.schoolId, subjectId: currentUser?.subjectId });
        setError('학교 또는 교과 정보를 찾을 수 없습니다.');
        return;
      }

      console.log('포인트 내역 조회:', {
        schoolId: currentUser.schoolId,
        subjectId: currentUser.subjectId,
        studentId: student.id
      });

      // 도장 내역 가져오기 (studentId 필드 사용)
      const historyRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/stampHistories`);
      const q = query(historyRef, where('studentId', '==', student.id), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      console.log('조회된 포인트 내역 수:', snapshot.size);
      
      const history = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('포인트 내역 데이터:', data);
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate()
        };
      }) as PointHistory[];
      
      setPointHistory(history);
    } catch (err) {
      console.error('포인트 내역을 불러오는 중 오류:', err);
      setError('포인트 내역을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // 탭 변경 처리
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 승인 요청 다이얼로그 열기
  const handleOpenApprovalDialog = (request: ItemApprovalRequest) => {
    setSelectedRequest(request);
    setOpenApprovalDialog(true);
  };
  // 담당 교실 만들기 다이얼로그 열기
  const handleOpenAddNewClassDialog = () => {
    setOpenAddNewClassDialog(true);
  };
  // 승인 요청 처리
  const handleApprovalRequest = async (request: ItemApprovalRequest, approved: boolean, reason?: string) => {
    console.log('handleApprovalRequest 호출됨');
    console.log('승인 여부:', approved);
    console.log('요청 데이터:', request);
    console.log('학생 학급 ID:', request.studentClassId);
    console.log('현재 교사 정보:', currentUser);
    
    if (!currentUser?.schoolId || !isTeacher(currentUser)) {
      console.error('교사 권한이 없거나 학교 ID가 없습니다.');
      return;
    }

    if (request.targetType === 'subject' && !currentUser.subjectId) {
      console.error('교과 정보가 없습니다. 교과를 먼저 설정해주세요.');
      setError('교과를 먼저 설정해주세요.');
      return;
    }
    
    if (!request.studentClassId) {
      console.error('학생의 학급 정보가 없습니다.');
      setError('학생의 학급 정보가 없습니다.');
      return;
    }

    try {
      const batch = writeBatch(db);
      
      // 승인 요청 상태 업데이트
      const requestPath = request.targetType === 'class' 
        ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/itemApprovals/${request.id}`
        : `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/subjectitemApprovals/${request.id}`;
      
      console.log('승인 요청 경로:', requestPath);
      const requestRef = doc(db, requestPath);
      batch.update(requestRef, {
        status: approved ? 'approved' : 'rejected',
        updatedAt: serverTimestamp(),
        reason: reason || null,
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp()
      });

      // 학생 정보를 찾아서 아이템 상태 처리
      const studentQuery = query(
        collectionGroup(db, 'students'),
        where('uid', '==', request.studentId),
        where('schoolId', '==', currentUser.schoolId)
      );
      
      const studentSnapshot = await getDocs(studentQuery);
      console.log('학생 조회 결과:', studentSnapshot.size, '개');
      
      if (!studentSnapshot.empty) {
        const studentDoc = studentSnapshot.docs[0];
        const studentData = studentDoc.data();
        console.log('학생 데이터:', studentData);
        
        const studentItems = studentData.items || {};
        // 고유키별로 itemId가 일치하는 항목을 찾는다
        const allItems = Object.entries(studentItems) as [string, any][];
        const matching = allItems.filter(([key, value]) =>
          key === request.itemId  // uniqueKey와 직접 비교
        );
        console.log('일치하는 아이템들:', matching);
        if (matching.length === 0) {
          console.error('아이템 정보가 없습니다.');
          setError('아이템 정보가 없습니다.');
          return;
        } else {
          const [useKey] = matching[0];
          
          if (approved) {
            // 승인된 경우: 아이템을 삭제하고 사용 권한 부여
            batch.update(studentDoc.ref, {
              [`items.${useKey}`]: deleteField()
            });
            
            // 아이템 사용 권한 부여 (승인 요청 ID를 포함하여 고유성 보장)
            const usagePath = request.targetType === 'class'
              ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/itemUsages/${request.id}`
              : `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/subjectitemUsages/${request.id}`;
            console.log('아이템 사용 권한 경로:', usagePath);
            const itemUsageRef = doc(db, usagePath);
            batch.set(itemUsageRef, {
              studentId: request.studentId,
              itemId: request.itemId,
              approvalRequestId: request.id,
              approvedAt: serverTimestamp(),
              approvedBy: currentUser.uid,
              status: 'active'
            });
          } else {
            // 거절된 경우: 아이템 상태를 available로 되돌리기
            batch.update(studentDoc.ref, {
              [`items.${useKey}.status`]: 'available'
            });
          }
        }
      } else {
        console.error('학생 정보를 찾을 수 없습니다.');
        setError('학생 정보를 찾을 수 없습니다.');
        return;
      }

      console.log('배치 작업 실행 전');
      await batch.commit();
      console.log('배치 작업 완료');
      
      setOpenApprovalDialog(false);
    } catch (err) {
      console.error('승인 처리 중 오류:', err);
      setError('승인 처리 중 오류가 발생했습니다.');
    }
  };

  if (!currentUser || (!isTeacher(currentUser) && !isSuperAdmin(currentUser))) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        {loading ? (
          <div>로딩 중...</div>
        ) : (
          <div style={{ 
            color: 'red', 
            padding: '20px', 
            border: '1px solid red', 
            borderRadius: '4px' 
          }}>
            교사 권한이 필요합니다.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
    }}>
      {/* 학교 로고 및 학교 이름 표시*/}
      <div style={{
         display: 'flex',
         alignItems: 'center',
         gap: 'var(--gap-level-3, 8px)',
         alignSelf: 'stretch',
      }}>
        {schoolInfo?.logo ? (
          <img 
            src={schoolInfo.logo} 
            alt={`${schoolInfo.name} 로고`}
            style={{
              width: '24px',
              height: '24px',
              objectFit: 'contain',
              borderRadius: '4px'
            }}
            onError={(e) => {
              // 이미지 로딩 실패 시 기본 아이콘으로 대체
              e.currentTarget.style.display = 'none';
              const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
              if (nextElement) {
                nextElement.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div 
          style={{
            width: '24px',
            height: '24px',
            backgroundColor: '#1976d2',
            borderRadius: '4px',
            display: schoolInfo?.logo ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            fontWeight: 'bold'
          }}
        >
          🏫
        </div>
        <div style={{ 
          margin: 0,
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Body-2, 16px)',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '24px'
        }}>
          {schoolInfo?.name || '학교 정보를 불러오는 중...'}
        </div>
      </div>
        {/* 인사말 헤더*/}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gap-level-3, 8px)',
        alignSelf: 'stretch',
        padding: '8px 0px 32px 0px'
      }}>
        <h1 style={{ 
          color: 'var(--Color-text-normal, #161616)',
          /* Heading 2/Semibold */
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Heading-2, 32px)',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
          letterSpacing: '-0.64px',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          만나서 반갑습니다, {currentUser.name}님
          {isTeacher(currentUser) && (currentUser as Teacher).subjectId && (
            <>
              <DividerIcon width={2} height={28} color="#BABABA" />
              <span style={{
                color: 'var(--Color-fill-brand-color, #FF6F0F)',
                fontSize: 'var(--Font-Font-size-Heading-2, 32px)',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                letterSpacing: '-0.64px'
              }}>
                {getSubjectName((currentUser as Teacher).subjectId!)}
              </span>
            </>
          )}
        </h1>
      </div>
      {/* 중요 알림 창*/}
      <div style={{ 
        width: '100%',
        height: isMobile ? 'auto' : '40px',
        minHeight: isMobile ? 'auto' : '40px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '12px' : '18px', // 모바일: 간격 축소
        paddingLeft: isMobile ? '0' : '0',
        paddingRight: isMobile ? '0' : '0',
        flexShrink: 0, // 모바일: 축소 방지
      }}>
      <div style={{ 
        display: 'flex',
        width: isMobile ? '100%' : '1172px',
        height: isMobile ? 'auto' : '100%',
        minHeight: isMobile ? '40px' : '100%',
        alignItems: 'center',
        gap: '8px',
        borderRadius: '8px',
        border: '1.2px solid #293044',
        background: '#293044',
        padding: isMobile ? '12px' : '0',
      }}>
         <div style={{
           display: 'flex',
           marginLeft: '8px',
           justifyContent: 'center',
           alignItems: 'center',
           width: '40px',
           height: '22px',
           flexShrink: 0,
           borderRadius: '8px',
           border: '1px solid #F0F0F0',
           background: '#FFF'
         }}>
          <span style={{
            color: 'var(--Color-status-negative, #FF3838)',
            /* Label 2/Medium */
            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
            fontSize: 'var(--Font-Font-size-Label-2, 12px)',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '150%' /* 18px */
          }}>
            중요
          </span>
         </div>
        <p style={{
          color: 'var(--Color-text-inverse, #FFF)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Body-4, 13px)',
          fontStyle: 'normal',
          fontWeight: '500',
          lineHeight: '150%',
          margin: 0,
          flex: 1,
        }}>
          현재 UI 변경 중에 있습니다. 참고부탁드립니다.
        </p>
      </div>
        <Button
          width={isMobile ? "100%" : "112px"}
          height={isMobile ? "40px" : "100%"}
          size="medium"
          variant="submit"
          onClick={() => {}}
          leftIcon={<PlusCircleIcon width={14} height={13} color="white" />}
        >
          알림 등록
        </Button>
      </div>
      
      {/* 대시보드 전체 영역*/}
      <div
      style={{
        width: '1280px',
        height: '642px',
        gap: '24px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-start', 
      }}
      >
        {/* 반, 일정, 포인트, 사용 승인 요청 영역*/}
        <div style={{
          width: '845px',
          height: '642px',
          gap: '24px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{
            width: '100%',
            height: '309px',
            gap: '24px',
            display: 'flex',
            flexDirection: 'row',
          }}>
            {/* 교과 현황 카드*/}
            <Card
              width="193px"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ display: 'inline-flex' }}
            >
          {/* 교과 현황 정보 영역*/}
            <div style={{
              width: '100%',
              height: '100%',
          flexDirection: 'column', 
          justifyContent: 'flex-start', 
          alignItems: 'flex-start', 
          gap: 55, 
              display: 'flex'
              }}>
                {/* 교과 현황 + 나의 교과 교실 영역 (gap: 19px) */}
                <div style={{
                  width: '100%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 19,
                  display: 'flex'
                }}>
                  {/* 교과 현황 제목 영역 */}
                  <div style={{
                    width: '100%',
                    alignSelf: 'stretch', 
                    flexDirection: 'column', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: 2, 
                    display: 'flex'
                    }}>
                    <div style={{
                      alignSelf: 'stretch', 
                      color: 'var(--Color-text-normal, #161616)', 
                      fontSize: '18px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '600', 
                      lineHeight: '27px', 
                      wordWrap: 'break-word'}}>
                        교과 현황
                      </div>
                    <div style={{
                      alignSelf: 'stretch', 
                      color: 'var(--Color-text-assistive, #989898)', 
                      fontSize: '14px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '400', 
                      lineHeight: '21px', 
                      wordWrap: 'break-word',
                      margin: 0
                      }}>
                        {new Date().getFullYear()}.{String(new Date().getMonth() + 1).padStart(2, '0')}.{String(new Date().getDate()).padStart(2, '0')}-{String(new Date().getMonth() + 1).padStart(2, '0')}.{String(new Date().getDate()).padStart(2, '0')}
                    </div>
                  </div>
                  
                  {/* 나의 교과 교실 영역 */}
                  <div style={{
                    alignSelf: 'stretch', 
                    flexDirection: 'column', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: 16, 
                    display: 'flex'}}>
                    <div style={{
                      alignSelf: 'stretch', 
                      color: 'var(--Color-text-normal, #161616)', 
                      fontSize: '14px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '600', 
                      lineHeight: '21px', 
                      wordWrap: 'break-word'}}>
                      나의 교과 교실
                    </div>
                    <div style={{
                      width: '142px',
                      height: '76px',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      overflowX: 'auto',
                      overflowY: 'hidden'
                    }}>
                      {subjectClasses.length > 0 ? (
                        subjectClasses.slice(0, 3).map((classInfo, index) => (
                          <div 
                            key={classInfo.id}
                            style={{
                              width: '54px',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              gap: 4,
                              display: 'flex',
                              flexShrink: 0
                            }}
                          >
                            <div style={{
                              width: '54px',
                              height: '54px',
                              background: 'var(--Color-background-sub, #F8F8F8)',
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <img 
                                src={'/images/class/ClassSampleImage.png'}
                                alt={`${classInfo.grade}학년 ${classInfo.class}반`}
                                style={{
                                  width: '36px',
                                  height: '35px',
                                  objectFit: 'cover',
                                  borderRadius: 4
                                }}
                              />
                            </div>
                            <div style={{
                              alignSelf: 'stretch',
                              textAlign: 'center',
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px',
                              wordWrap: 'break-word'
                            }}>
                              {classInfo.grade}학년 {classInfo.class}반
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard'
                        }}>
                          교실 없음
                        </div>
                      )}
                    </div>
                    {subjectClasses.length > 3 && (
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: 'var(--Color-background-normal, white)',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
                        borderRadius: '9999px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '12px'
                      }}>
                        →
                      </div>
                    )}
                  </div>
                </div>

                {/* 담당 교실 만들기 버튼 */}
                <Button
                  width="100%"
                  height="40px"
                  size="medium"
                  variant="primary"
                  onClick={() => {
                    handleOpenAddNewClassDialog();
                  }}
                >
                  담당 교실 만들기
                </Button>
                            </div>
            </Card>
            {/* 일정 정보 카드 */}
            <Card
              width="628px"
              height="309px"
              padding="16px 20px"
              flexDirection="row"
              alignItems="flex-start"
              gap="27px"
            >
              <div style={{
                flex: '0 0 233px', 
                height: '100%', 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: '16px'
                }}>
                  <div style={{
                    width: '100%', 
                    height: '50px', 
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    flexDirection: 'column',
                    gap: '2px'}}>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-normal, #161616)',
                      fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                      fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '150%', /* 27px */
                      letterSpacing: '-0.18px',
                      margin: 0
                    }}>
                      {new Date().getFullYear()}년 {new Date().getMonth() + 1}월
                        </div>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                      fontSize: 'var(--Font-Font-size-Body-3, 14px)',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '150%',
                      margin: 0
                    }}>
                      {/* 3월 ~ 7월 : 1학기, 9월 ~ 2월 : 2학기 */}
                      {new Date().getFullYear()}년 {new Date().getMonth() + 1 >= 3 && new Date().getMonth() + 1 <= 7 ? '1학기' : '2학기'}
                    </div>
                </div>
                  {/* 일정 내용 영역 */}
                  <div style={{
                    display: 'flex',
                    height: '211px',
                    padding: '16px',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                    borderRadius: '12px',
                    background: 'var(--Color-background-sub, #F8F8F8)',
                  }}>
                    {/* 상단 일, 요일, 일정 개수 내용 영역 */}
                    <div style={{
                      width: '100%',
                      height: '43px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: '4px'
                    }}>
                      {/* 일정 내용 */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '7px',
                        height: '21px'
                      }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          padding: '1.6px 2.27px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img 
                            src={'/images/icons/calendar.svg'} 
                            alt="달력 아이콘"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                </div>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: '600',
                          lineHeight: '150%',
                          margin: 0
                        }}>
                          {new Date().getDate()}일 {['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]}요일 일정
                        </span>
            </div>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                        fontSize: '12px',
                        textAlign: 'center',
                        margin: 0
                      }}>
                        오늘, 내 일정이 ~개 있습니다.
        </div>
</div>  
                      {/* 하단 일정 내용 영역 */}
                      <div style={{ 
                        width: '100%',
                        height: '168px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '16px',
                        overflow: 'auto',
                      }}>
                        {/* 일정 목록 */}
                        {schedules.length > 0 ? (
                          schedules.map((schedule) => (
                            <div 
                              key={schedule.id}
                              style={{ 
                                width: '100%',
                                minHeight: '42px',
                                maxHeight: '42px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                gap: '4px',
                                padding: '8px 0px'
                              }}
                            >
                              <div style={{
                                width: '100%',
                                height: '20px',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '9px'
                              }}>
                                <img src={'/images/icons/circleIcon.svg'} alt="원 아이콘"/>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                  fontSize: '13px',
                                  fontStyle: 'normal',
                                  fontWeight: '600',
                                  lineHeight: '150%',
                                  margin: 0
                                }}>
                                  {schedule.title}
                                </span>
                                {schedule.time && (
                                  <span style={{
                                    color: 'var(--Color-text-assistive, #989898)',
                                    fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                    fontSize: '12px',
                                    fontWeight: '400',
                                    marginLeft: 'auto'
                                  }}>
                                    {schedule.time}
                                  </span>
                                )}
                              </div>
                              <p style={{
                                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                                fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                fontSize: 'var(--Font-Font-size-Label-2, 12px)',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: '150%',
                                margin: 0
                              }}>
                                {schedule.description}
                              </p>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--Color-text-assistive, #989898)',
                            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                            fontSize: '12px'
                          }}>
                            오늘 일정이 없습니다.
                          </div>
                        )}
                  </div>

                  </div>
              </div>
              {/* 달력 */}
              <div style={{ 
                width: '328px',
                height: '277px',
                display: 'flex', 
                justifyContent: 'flex-start',
                alignItems: 'center',
                flexDirection: 'column',
                padding: '17px 8px 4px 8px',
                gap: '12px'
              }}>
                <div data-block="calendar-header" style={{ 
                  width: '100%',
                  height: '20px',
                  flexShrink: '0',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flexDirection: 'row',
                  gap: '36px',
                  padding: '0px 6px 0px 6px',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%',
                }}>
                  <span>일</span>
                  <span>월</span>
                  <span>화</span>
                  <span>수</span>
                  <span>목</span>
                  <span>금</span>
                  <span>토</span>
                </div>
                {/* 달력 그리드 */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  columnGap: '24px',
                  rowGap: '4px'
                }}>
                  {generateCalendar().map((day, index) => (
                    <div 
                      key={index}
            style={{ 
              display: 'flex',
                        width: '24px',
                        height: '34px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px'
                      }}
                    >
                      {/* 날짜 기입 영역 */}
                      <div style={{
                        display: 'flex', 
                        height: '24px',
                        padding: '3px',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: '0',
                        aspectRatio: '1/1',   
                        color: day.isCurrentMonth 
                          ? (day.isToday ? 'var(--Color-text-inverse, #FFF)' : 'var(--Color-text-normal, #161616)')
                          : 'var(--Color-text-assistive, #989898)',
                        backgroundColor: day.isToday ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                        borderRadius: day.isToday ? '50%' : '0',
                        textAlign: 'center',
                        fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                        fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '150%'
                      }}>
                        {day.date}
                      </div>
                      {/* 일정 아이콘 */}
                      <div style={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {day.isCurrentMonth && schedules.some(schedule => schedule.date === day.date) && (
                          <img src={'/images/icons/circleSky.svg'} alt="일정 있음"/>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card> 
          </div>
          <div style={{
            width: '100%',
            height: '309px',
            display: 'flex',
            flexDirection: 'row',
            gap: '25px'
          }}>
            {/* 포인트 */}
            <Card
              width="302px"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ display: 'inline-flex' }}
            >
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {/* 제목 영역 */}
                <div data-block="stamp-rankings-header" style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div data-block="stamp-rankings-header-title" style={{
                    width: '100%',
                    height: '27px',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'var(--Color-text-normal, #161616)',
                    fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                    fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    lineHeight: '150%', /* 27px */
                    letterSpacing: '-0.18px',
                    margin: 0
                  }}>
                    도장 현황
                  </div>
                  
                  {/* 학년 필터 */}
                  <div data-block="stamp-rankings-header-filter" style={{
                    width: '100%',
                    height: 20,
                    minHeight: 20,
                    maxHeight: 20,
                    flexShrink: 0,
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 6,
                    display: 'flex'
                  }}>
                    <div 
                      data-block="stamp-rankings-header-filter-all"
                      onClick={() => setStampGradeFilter('all')}
                      style={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        height: 20,
                        minHeight: 20,
                        gap: 4,
                        display: 'flex',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        textAlign: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        color: stampGradeFilter === 'all' ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                        fontSize: 13,
                        fontFamily: 'pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px',
                        wordWrap: 'break-word'
                      }}>
                        전체
                      </div>
                      <div style={{
                        height: 16,
                        paddingLeft: 4,
                        paddingRight: 4,
                        paddingTop: 2,
                        paddingBottom: 2,
                        background: stampGradeFilter === 'all' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                        borderRadius: 999,
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 10,
                        display: 'flex'
                      }}>
                        <div style={{
                          textAlign: 'center',
                          justifyContent: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          color: 'var(--Color-text-inverse, white)',
                          fontSize: 11,
                          fontFamily: 'pretendard',
                          fontWeight: '400',
                          lineHeight: '11px',
                          wordWrap: 'break-word'
                        }}>
                          {stampRankings.length}
                        </div>
                      </div>
                    </div>
                    {[1, 2, 3].map((grade) => {
                      const gradeCount = stampRankings.filter(student => student.grade === grade).length;
                      return (
                        <div
                          key={grade}
                          onClick={() => setStampGradeFilter(grade as 1 | 2 | 3)}
                          style={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 4,
                            display: 'flex',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{
                            textAlign: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            color: stampGradeFilter === grade ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                            fontSize: 13,
                            fontFamily: 'pretendard',
                            fontWeight: '500',
                            lineHeight: '19.50px',
                            wordWrap: 'break-word'
                          }}>
                            {grade}학년
                          </div>
                          <div style={{
                            height: 16,
                            paddingLeft: 4,
                            paddingRight: 4,
                            paddingTop: 2,
                            paddingBottom: 2,
                            background: stampGradeFilter === grade ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                            borderRadius: 999,
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 10,
                            display: 'flex'
                          }}>
                            <div style={{
                              textAlign: 'center',
                              justifyContent: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              color: 'var(--Color-text-inverse, white)',
                              fontSize: 11,
                              fontFamily: 'pretendard',
                              fontWeight: '400',
                              lineHeight: '11px',
                              wordWrap: 'break-word'
                            }}>
                              {gradeCount}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  overflow: 'hidden'
                }}>
                  {/* 포인트 표 제목 영역*/}
                <div style={{
                  width: '100%',
                  height: '28px',
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '4px 8px',
                  gap: '12px',
                  borderRadius: 'var(--common-8, 8px)',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%', /* 19.5px */
                }}> 
                  <div style={{
                    width: '40px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    순위
                  </div>
                  <div style={{
                    width: '90px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    이름
                  </div>
                  <div style={{
                    width: '29px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    번호
                  </div>
                  <div style={{
                    width: '47px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    도장
                  </div>
                </div>
                {/* 도장 표 내용 영역*/}
                <div style={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}>
                   {(() => {
                     const filteredRankings = stampGradeFilter === 'all' 
                       ? stampRankings 
                       : stampRankings.filter(student => student.grade === stampGradeFilter);
                     
                     return filteredRankings.map((student, index) => (
                     <div 
                       key={index}
                style={{ 
                         width: '100%',
                         height: '38px',
                         padding: '8px',
                         borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                         gap: '12px',
                         display: 'flex',
                         flexDirection: 'row'
                       }}
                     >
                         {index < 3 ? (
                           <div style={{
                  width: '40px',
                             display: 'flex',
                             height: '22px',
                             padding: '2px 12px',
                             justifyContent: 'center',
                             alignItems: 'center',
                             gap: '4px',
                             borderRadius: '8px',
                             background: index === 0 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 
                                        index === 1 ? '#FF9F5E' : 
                                        '#FFD2B5'
                           }}>
                             <div style={{
                               height: '18px',
                               width: 'auto',
                               minWidth: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                               color: 'var(--Color-text-inverse, #FFF)',
                               fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                               fontSize: 'var(--Font-Font-size-Label-2, 12px)',
                               margin: '0px',
                               fontStyle: 'normal',
                               fontWeight: '500',
                               lineHeight: '150%',
                               whiteSpace: 'nowrap'
                             }}>
                               {index + 1}위
                             </div>
                           </div>
                         ) : (
                           <div style={{
                             width: '40px',
                             display: 'flex',
                             height: '22px',
                             padding: '2px 12px',
                             justifyContent: 'center',
                             alignItems: 'center',
                             gap: '4px',
                             color: 'var(--Color-text-normal, #161616)',
                             fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                             fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                             fontStyle: 'normal',
                             fontWeight: '400',
                             lineHeight: '150%',
                             whiteSpace: 'nowrap'
                           }}>
                             {index + 1}위
                           </div>
                         )}
                         <div style={{
                           width: '90px',
                           display: 'flex',
                           flexDirection: 'row',
                           gap: '4px',
                           alignItems: 'center',
                           flexShrink: '0'
                         }}>
                           <div style={{
                             height: '20px',
                             width: '20px',
                             display: 'flex',
                           }}>
                             <img src="/images/icons/profileimage.svg" alt="프로필 아이콘" />
                           </div>
                           <div style={{
                             width: '60px',
                             display: 'flex',
                             flexShrink: '0',
                             color: 'var(--Color-text-normal, #161616)',
                             fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                             fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                             fontStyle: 'normal',
                             fontWeight: '400',
                             lineHeight: '150%',
                             cursor: 'pointer',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}
                           onClick={() => handleStudentClick(student)}
                           title={student.name}
                           >
                             {student.name.length > 4 ? `${student.name.substring(0, 4)}...` : student.name}
              </div>
                         </div>
                         <div style={{
                           width: '29px',
                           display: 'flex',
                           alignItems: 'center',
                           color: 'var(--Color-text-normal, #161616)',
                           fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                           fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                           fontStyle: 'normal',
                           fontWeight: '400',
                           lineHeight: '150%',
                         }}>
                           {student.studentNumber || '-'}
            </div>
            <div style={{ 
                           width: '47px',
                           display: 'flex',
                           alignItems: 'center',
                           color: 'var(--Color-text-normal, #161616)',
                           fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                           fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                           fontStyle: 'normal',
                           fontWeight: '400',
                           lineHeight: '150%',
                         }}>
                           {isStampObject(student.stamps) ? student.stamps.count : student.stamps}
                         </div>
                     </div>
                     ));
                   })()}
                </div>
                </div>
              </div>
            </Card>
            {/* 아이템 승인 요청 */}
            <Card
              width="518px"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ display: 'inline-flex' }}
            >
              <div style={{ 
                width: '100%',
                height: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px'
              }}>
                {/* 제목 영역 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    width: '100%',
                    height: '27px',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'var(--Color-text-normal, #161616)',
                    fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                    fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    lineHeight: '150%', /* 27px */
                    letterSpacing: '-0.18px',
                    justifyContent: 'center',
                    margin: 0
                  }}>
                    아이템 사용 승인 요청
                  </div>
                  
                  {/* 학년 필터 */}
                  <div style={{
                    width: '100%',
                    height: '20px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 6,
                    display: 'inline-flex'
                  }}>
                    <div
                      onClick={() => setApprovalGradeFilter('all')}
                      style={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 4,
                        display: 'flex',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        textAlign: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        color: approvalGradeFilter === 'all' ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                        fontSize: 13,
                        fontFamily: 'pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px',
                        wordWrap: 'break-word'
                      }}>
                        전체
                      </div>
                      <div style={{
                        height: 16,
                        paddingLeft: 4,
                        paddingRight: 4,
                        paddingTop: 2,
                        paddingBottom: 2,
                        background: approvalGradeFilter === 'all' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                        borderRadius: 999,
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 10,
                        display: 'flex'
                      }}>
                        <div style={{
                          textAlign: 'center',
                          justifyContent: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          color: 'var(--Color-text-inverse, white)',
                          fontSize: 11,
                          fontFamily: 'pretendard',
                          fontWeight: '400',
                          lineHeight: '11px',
                          wordWrap: 'break-word'
                        }}>
                          {approvalRequests.length}
                        </div>
                      </div>
                    </div>
                    {[1, 2, 3].map((grade) => {
                      const gradeCount = approvalRequests.filter(req => req.studentGrade === grade).length;
                      return (
                        <div
                          key={grade}
                          onClick={() => setApprovalGradeFilter(grade as 1 | 2 | 3)}
                          style={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 4,
                            display: 'flex',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{
                            textAlign: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            color: approvalGradeFilter === grade ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                            fontSize: 13,
                            fontFamily: 'pretendard',
                            fontWeight: '500',
                            lineHeight: '19.50px',
                            wordWrap: 'break-word'
                          }}>
                            {grade}학년
                          </div>
                          <div style={{
                            height: 16,
                            paddingLeft: 4,
                            paddingRight: 4,
                            paddingTop: 2,
                            paddingBottom: 2,
                            background: approvalGradeFilter === grade ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)',
                            borderRadius: 999,
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 10,
                            display: 'flex'
                          }}>
                            <div style={{
                              textAlign: 'center',
                              justifyContent: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              color: 'var(--Color-text-inverse, white)',
                              fontSize: 11,
                              fontFamily: 'pretendard',
                              fontWeight: '400',
                              lineHeight: '11px',
                              wordWrap: 'break-word'
                            }}>
                              {gradeCount}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                    </div>
                
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  maxHeight: '300px'
                }}>
                  {/* 포인트 표 제목 영역*/}
                <div style={{
                  width: '100%',
                  height: '28px',
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '4px 8px',
                  gap: '24px',
                  borderRadius: 'var(--common-8, 8px)',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%', /* 19.5px */
                }}> 
                  <div style={{
                    width: '17px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    No
                    </div>
                  <div style={{
                    width: '45px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    이름
                    </div>
                  <div style={{
                    width: '136px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    아이템
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                  }}>
                    요청일시
                  </div>
                </div>
                {/* 포인트 표 내용 영역*/}
                {(() => {
                  const filteredRequests = approvalGradeFilter === 'all' 
                    ? approvalRequests 
                    : approvalRequests.filter(req => req.studentGrade === approvalGradeFilter);
                  
                  return filteredRequests.length > 0 ? (
                    filteredRequests.map((request, index) => (
                    <div key={request.id} style={{
                      width: '100%',
                      minHeight: '41px',
                      height: '41px',
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '8px',
                      borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',      
                      overflow: 'hidden',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{
                        width: '100%', 
                        minHeight: '25px',
                        height: '25px', 
                        gap: '24px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        flex: 1
                      }}>
                        <div style={{
                          width: '17px', 
                          minHeight: '25px',
                          height: '25px',
                          justifyContent: 'center', 
                          alignItems: 'flex-start',
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: '0',
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          wordWrap: 'break-word',
                          whiteSpace: 'nowrap'}}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div style={{
                          width: '45px', 
                          minHeight: '25px',
                          height: '25px',
                          justifyContent: 'center', 
                          alignItems: 'flex-start',
                          display: 'flex', 
                          flexShrink: '0',
                          flexDirection: 'column', 
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          wordWrap: 'break-word'}}>
                          {request.studentName}
                        </div>
                        <div style={{
                          width: '136px',
                          minHeight: '25px',
                          height: '25px',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: '0',
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          wordWrap: 'break-word',
                          whiteSpace: 'nowrap'}}>
                          {request.itemName}
                        </div>
                        <div style={{
                          justifyContent: 'center', 
                          alignItems: 'flex-start',
                          width: '103px',
                          minHeight: '25px',
                          height: '25px',
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: '0',
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          wordWrap: 'break-word',
                          whiteSpace: 'nowrap'}}>
                          {request.createdAt.toISOString().slice(0, 16).replace('T', ' ').replace(/-/g, '.')}
                        </div>
                        <div style={{
                          paddingLeft: '17px', 
                          paddingRight: '17px', 
                          paddingTop: '4px', 
                          paddingBottom: '4px', 
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          width: '54px', 
                          height: '25px',
                          flexShrink: '0',
                          background: 'var(--Color-fill-normal, #293044)', 
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSelectedRequest(request);
                          setOpenApprovalDialog(true);
                        }}>
                          <div style={{
                            color: 'white', 
                            fontSize: 11,
                            fontFamily: 'Pretendard', 
                            fontWeight: '400',
                            lineHeight: '16.50px',
                          }}>
                            승인
                </div>
              </div>
            </div>
          </div>
                    ))
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard'
                    }}>
                      승인 요청이 없습니다
                    </div>
                  );
                })()}
                  
                </div>
              </div>
            </Card>
          </div> 
        </div>
        {/* 반, 달력, 포인트, 아이탬 승인 영역*/}
        
        {/* 시간표 */}
        <Card 
        width="518px" 
        height="100%" 
        padding="16px 20px" 
        alignItems="flex-start" 
        gap={24} 
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* 시간표 제목 영역*/}
        <div style={{ 
            width: '100%',
            height: '50px',
            display: 'inline-flex',
            flexDirection: 'row',
            gap: '8px'
          }}>
            <div style={{
              width: '243px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div style={{alignSelf: 'stretch', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'var(--Color-text-normal, #161616)', fontSize: '18px', fontFamily: 'Pretendard', fontWeight: '600', lineHeight: '27px', wordWrap: 'break-word'}}>시간표</div>
              <div style={{alignSelf: 'stretch', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'var(--Color-text-assistive, #989898)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px', wordWrap: 'break-word'}}>{timetableData.year}년 {timetableData.semester}</div>
            </div>
            <Button 
                variant='edit'
                width='120px'
                height='40px'
                size='small'
                onClick={() => setIsTimetableDialogOpen(true)}
                leftIcon={<EditIcon width={16} height={16} color="#4C4C4C" />}
              >시간표 수정하기</Button>
              
          </div>
          {/* 시간표 내용 영역*/}
          <div style={{
            width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            gap: '16px'
          }}>
            {/* 시간표 현재 교과 안내 영역*/}
            <div style={{
              width: '100%', 
              justifyContent: 'flex-start', 
              alignItems: 'center',
                display: 'flex', 
              flexDirection: 'row',
              gap: '4px',
              color: 'var(--Color-text-normal, #161616)', 
              fontSize: '18px', 
              fontFamily: 'Pretendard', 
              fontWeight: '600', 
              lineHeight: '27px', 
              wordWrap: 'break-word'}}>
                지금은 
                <span style={{
                  color: 'var(--Color-text-brand-color, #FF6F0F)'}}>
                  1교시 통합 과학
                </span> 
                시간입니다
            </div>
            {/* 시간표 시간표 영역*/}
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {/* 시간표 요일 영역*/}
              <div style={{
              width: '100%',
              height: '28px',
              display: 'flex',
              flexDirection: 'row',
              gap: '50px',
              fontSize: '13px',
              fontFamily: 'Pretendard',
              fontWeight: '500',
              lineHeight: '19.50px',
              wordWrap: 'break-word',
              padding: '4px 29px 4px 82px', 
              background: 'var(--Color-background-sub, #F8F8F8)', 
              borderRadius: '8px', 
              justifyContent: 'center', 
              alignItems: 'center'
              }}>
                <div style={{
                  width: '12px',
                  height: '100%',
                  color: new Date().getDay() === 1 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  월
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 2 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  화
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 3 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  수
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 4 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  목
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 5 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  금
                </div>
              </div>
              {/* 시간표 시간표 영역*/}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {/* 동적으로 시간표 렌더링 */}
                {timetableData.entries.slice(0, 4).map((entry) => (
                  <div key={entry.period} style={{
                    height: '48px',
                    gap: '18px',
                    padding: '0px 8px 10px 8px',
                    display: 'inline-flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontFamily: 'Pretendard',
                    wordWrap: 'break-word',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    {/* 교시 및 시간*/}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '13px',
                        lineHeight: '19.50px',
                        display: 'flex',
                        color: 'var(--Color-text-normal, #161616)' 
                      }}>
                        {entry.period}교시
                      </div>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '12px',
                        lineHeight: '18px',
                        width: '33px',
                        flexShrink: '0',
                        display: 'flex',
                        color: 'var(--Color-text-assistive, #989898)' 
                      }}>
                        {entry.time}
                      </div>
                    </div>
                    {/* 과목 영역*/}
                    <div style={{
                      display: 'flex',
                      width: '305px',
                      height: '20px',
                      padding: '0px 4px',
                      gap: '13px',
                      fontSize: '13px',
                      flexDirection: 'row',
                      lineHeight: '19.50px',
                      wordWrap: 'break-word',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      flexShrink: '0'
                    }}>
                      <span style={{ width: '49px' }}>{entry.monday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.tuesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.wednesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.thursday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.friday || '-'}</span>
                    </div>
                  </div>
                ))}
                
                {/* 점심시간 */}
                <div style={{
                  height: '48px',
                  gap: '18px',
                  padding: '0px 8px 10px 8px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center', 
                  fontStyle: 'normal',
                  fontWeight: '500',
                  wordWrap: 'break-word',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  fontSize: '13px',
                  lineHeight: '19.50px',
                  color: 'var(--Color-text-normal, #161616)'
                }}>
                  😋 점심시간
                </div>

                {/* 5-7교시 */}
                {timetableData.entries.slice(4, 7).map((entry) => (
                  <div key={entry.period} style={{
                    height: '48px',
                    gap: '18px',
                    padding: '0px 8px 10px 8px',
                    display: 'inline-flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontFamily: 'Pretendard',
                    wordWrap: 'break-word',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    {/* 교시 및 시간*/}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '13px',
                        lineHeight: '19.50px',
                        display: 'flex',
                        color: 'var(--Color-text-normal, #161616)' 
                      }}>
                        {entry.period}교시
                      </div>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '12px',
                        lineHeight: '18px',
                        width: '33px',
                        flexShrink: '0',
                        display: 'flex',
                        color: 'var(--Color-text-assistive, #989898)' 
                      }}>
                        {entry.time}
                      </div>
                    </div>
                    {/* 과목 영역*/}
                    <div style={{
                      display: 'flex',
                      width: '305px',
                      height: '20px',
                      padding: '0px 4px',
                      gap: '13px',
                      fontSize: '13px',
                      flexDirection: 'row',
                      lineHeight: '19.50px',
                      wordWrap: 'break-word',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      flexShrink: '0'
                    }}>
                      <span style={{ width: '49px' }}>{entry.monday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.tuesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.wednesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.thursday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.friday || '-'}</span>
                    </div>
                  </div>
                ))}
            </div>
            
          </div>
          </div>
        </Card>
      </div>
      
      {/* 학생 상세 정보 다이얼로그 */}
      {openStudentDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '16px 20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px', fontSize: '20px' }}>👤</span>
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                  {selectedStudent?.name} 학생 정보
                </h3>
              </div>
              <button 
                onClick={() => setOpenStudentDialog(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 0, flex: 1, overflow: 'auto' }}>
              <div style={{ borderBottom: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', width: '100%' }}>
                  <button
                    onClick={() => setTabValue(0)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: 'none',
                      backgroundColor: tabValue === 0 ? '#1976d2' : 'transparent',
                      color: tabValue === 0 ? 'white' : '#666',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    포인트 내역
                  </button>
                </div>
              </div>
              
              {/* 포인트 내역 탭 */}
              {tabValue === 0 && (
                <div style={{ padding: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '1.125rem' }}>
                      현재 포인트: <span style={{ 
                        backgroundColor: '#1976d2', 
                        color: 'white', 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                      }}>
                        {selectedStudent?.points}
                      </span>
                    </h4>
                  </div>
                  
                  {loadingHistory ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                      <div>로딩 중...</div>
                    </div>
                  ) : pointHistory.length > 0 ? (
                    <div>
                      {pointHistory.map((history) => (
                        <div 
                          key={history.id}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 0',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ 
                            backgroundColor: history.amount > 0 ? '#4caf50' : '#f44336',
                            color: 'white',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '16px',
                            fontSize: '18px'
                          }}>
                            {history.amount > 0 ? '+' : '-'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '1rem',
                              fontWeight: '500',
                              marginBottom: '4px'
                            }}>
                              {history.amount > 0 ? '포인트 지급' : '포인트 차감'} - {history.amount}점
                            </div>
                            <div style={{ 
                              fontSize: '0.875rem',
                              color: '#666',
                              marginBottom: '2px'
                            }}>
                              {history.reason}
                            </div>
                            <div style={{ 
                              fontSize: '0.75rem',
                              color: '#999'
                            }}>
                              {history.createdAt.toLocaleString()} | {history.teacherName} 교사
                            </div>
                          </div>
                          <div>
                            <span style={{ 
                              backgroundColor: history.amount > 0 ? '#4caf50' : '#f44336',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              {history.amount > 0 ? `+${history.amount}` : `${history.amount}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '32px'
                }}>
                  <div style={{ fontSize: '60px', color: '#666', marginBottom: '16px' }}>📊</div>
                  <p style={{ color: '#666', margin: 0, fontSize: '1rem' }}>
                    포인트 내역이 없습니다.
                  </p>
                </div>
              )}
                </div>
              )}
            </div>
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                onClick={() => setOpenStudentDialog(false)}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 승인 요청 다이얼로그 */}
      {openApprovalDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              padding: '20px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>
                아이템 사용 승인 요청
              </h3>
            </div>
            <div style={{ padding: '20px', flex: 1, overflow: 'auto' }}>
              {selectedRequest && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>
                      학생: {selectedRequest.studentName}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>
                      아이템: {selectedRequest.itemName}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: '#666' }}>
                      아이템 타입: {selectedRequest.targetType === 'class' ? '학급 아이템' : '교과 아이템'}
                    </p>
                    <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', color: '#666' }}>
                      요청일시: {selectedRequest.createdAt.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '0.875rem', 
                      fontWeight: '500' 
                    }}>
                      거절 사유 (선택)
                    </label>
                    <textarea
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                      placeholder="거절 사유를 입력하세요..."
                      onChange={(e) => setSelectedRequest({
                        ...selectedRequest,
                        reason: e.target.value
                      })}
                    />
                  </div>
                </div>
              )}
            </div>
            <div style={{ 
              padding: '20px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button 
                onClick={() => setOpenApprovalDialog(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                취소
              </button>
              <button 
                onClick={() => selectedRequest && handleApprovalRequest(selectedRequest, false, selectedRequest.reason)}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                거절
              </button>
              <button 
                onClick={() => {
                  console.log('승인 버튼 클릭됨');
                  console.log('selectedRequest:', selectedRequest);
                  if (selectedRequest) {
                    handleApprovalRequest(selectedRequest, true);
                  }
                }}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                승인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 시간표 수정 다이얼로그 */}
      <TimetableEditDialog
        isOpen={isTimetableDialogOpen}
        initialData={timetableData}
        neisContext={
          schoolInfo?.neisOfficeCode && schoolInfo?.neisSchoolCode && subjectClasses.length > 0
            ? {
                neisOfficeCode: schoolInfo.neisOfficeCode,
                neisSchoolCode: schoolInfo.neisSchoolCode,
                schoolType: schoolInfo.type,
                grade: subjectClasses[0].grade,
                classNum: subjectClasses[0].class,
              }
            : null
        }
        comciganContext={
          schoolInfo?.comciganSchoolCode && subjectClasses.length > 0
            ? {
                schoolCode: schoolInfo.comciganSchoolCode,
                grade: subjectClasses[0].grade,
                classNum: subjectClasses[0].class,
              }
            : null
        }
        onClose={() => setIsTimetableDialogOpen(false)}
        onTimetableSaved={async (timetable) => {
          try {
            if (!currentUser?.schoolId || !currentUser?.uid) {
              setError('학교 또는 교사 정보가 없습니다.');
              return;
            }

            // Firestore에 시간표 저장 (교사별 교과 시간표)
            const timetableRef = doc(db, `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectTimetable/current`);
            await setDoc(timetableRef, {
              semester: timetable.semester,
              year: timetable.year,
              entries: timetable.entries,
              subject: isTeacher(currentUser) ? (currentUser as Teacher).subject : null,
              subjectId: currentUser.subjectId,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser.uid,
              updatedByName: currentUser.name
            });

            console.log('교과 시간표가 Firebase에 저장되었습니다:', timetable);
            
            // 로컬 상태 업데이트
            setTimetableData(timetable);
            setIsTimetableDialogOpen(false);
            setSuccess('시간표가 성공적으로 저장되었습니다.');
          } catch (err) {
            console.error('시간표 저장 중 오류:', err);
            setError('시간표 저장 중 오류가 발생했습니다.');
          }
        }}
      />

      {/* 담당 교실 만들기 다이얼로그 */}
      <AddNewClassDialog
        isOpen={openAddNewClassDialog}
        onClose={() => setOpenAddNewClassDialog(false)}
        onAdd={async (data) => {
          if (!currentUser || !isTeacher(currentUser)) return;

          try {
            if (!data.code) {
              setError('교실 코드가 생성되지 않았습니다.');
              return;
            }

            const subjectCodeRef = collection(db, COLLECTIONS.SUBJECT_CODES);
            const existingQuery = query(
              subjectCodeRef,
              where('code', '==', data.code),
              where('schoolId', '==', currentUser.schoolId),
              where('subjectId', '==', currentUser.subjectId)
            );
            const existingSnapshot = await getDocs(existingQuery);

            if (existingSnapshot.empty) {
              await addDoc(collection(db, COLLECTIONS.SUBJECT_CODES), {
                code: data.code,
                schoolId: currentUser.schoolId,
                subjectId: currentUser.subjectId,
                teacherId: currentUser.uid,
                grade: data.grade,
                class: data.class,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
            }

            const teacherClassRef = doc(
              db,
              `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectClasses/${data.grade}_${data.class}`
            );
            await setDoc(teacherClassRef, {
              grade: data.grade,
              class: data.class,
              code: data.code,
              subjectId: currentUser.subjectId,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            }, { merge: true });

            setSuccess(`${data.grade}학년 ${data.class}반 교실이 추가되었습니다.`);
            setOpenAddNewClassDialog(false);
            await refetchSubjectClasses();
          } catch (err) {
            console.error('교실 추가 오류:', err);
            setError('교실 추가 중 오류가 발생했습니다.');
          }
        }}
      />
      </div>
  );
};

export default SubjectTeacherHome;